import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Info, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EducationalDialog } from './EducationalDialog';

export const LandUseRestrictionsCard: React.FC = () => {
  const [showDialog, setShowDialog] = useState(false);

  return (
    <>
      <Card className="h-full bg-card border-border">
        <CardHeader className="pb-2 md:pb-3">
          <CardTitle className="text-sm md:text-base flex items-center gap-1.5 md:gap-2">
            <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary flex-shrink-0" />
            <span className="hidden sm:inline">Land Use & Zoning Restrictions</span>
            <span className="sm:hidden">Zoning & Restrictions</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 md:space-y-3">
          <p className="text-xs md:text-sm text-muted-foreground">
            Zoning, HOAs, and Texas land use regulations.
          </p>

          <div className="grid gap-1.5 md:gap-2">
            <div className="p-2 md:p-3 rounded-lg bg-muted/30">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1">Residential Zoning</h4>
              <ul className="text-[10px] md:text-xs text-muted-foreground space-y-0.5 md:space-y-1">
                <li>• <strong>R1:</strong> Single-family only</li>
                <li>• <strong>R2:</strong> Duplexes, townhomes</li>
                <li>• <strong>R3:</strong> Multi-family apartments</li>
                <li>• <strong>MU:</strong> Mixed-use (res + comm)</li>
              </ul>
            </div>

            <div className="p-2 md:p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1 flex items-center gap-1.5">
                <Home className="w-3 h-3 flex-shrink-0" />
                Houston: No Zoning!
              </h4>
              <p className="text-[10px] md:text-xs text-muted-foreground">
                Deed restrictions control land use. Always review carefully.
              </p>
            </div>

            <div className="p-2 md:p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1">HOA Restrictions</h4>
              <ul className="text-[10px] md:text-xs text-muted-foreground space-y-0.5 md:space-y-1">
                <li>• Architectural guidelines</li>
                <li>• Rental restrictions</li>
                <li>• Commercial prohibitions</li>
                <li>• Parking & storage rules</li>
                <li>• Landscaping requirements</li>
              </ul>
            </div>

            <div className="p-2 md:p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1">Agricultural Exemptions</h4>
              <ul className="text-[10px] md:text-xs text-muted-foreground space-y-0.5 md:space-y-1">
                <li>• Requires 10+ acres</li>
                <li>• Ag use (cattle, hay, bees)</li>
                <li>• Wildlife alternative</li>
                <li>• Rollback taxes if converted</li>
              </ul>
            </div>

            <div className="p-2 md:p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1">Building Restrictions</h4>
              <ul className="text-[10px] md:text-xs text-muted-foreground space-y-0.5 md:space-y-1">
                <li>• Setbacks: 5-25 ft typical</li>
                <li>• Height: Usually 35 ft</li>
                <li>• Impervious: Max 50-65%</li>
                <li>• Tree preservation rules</li>
              </ul>
            </div>
          </div>

          <Button 
            variant="outline" 
            size="sm" 
            className="w-full h-8 text-xs md:text-sm"
            onClick={() => setShowDialog(true)}
          >
            <Info className="w-3 h-3 mr-1.5 md:mr-2" />
            <span className="hidden sm:inline">Complete Zoning & Restrictions Guide</span>
            <span className="sm:hidden">Learn More</span>
          </Button>
        </CardContent>
      </Card>

      <EducationalDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        title="Complete Guide to Land Use & Zoning in Texas"
      >
        <div className="space-y-4">
          <section>
            <h4 className="font-semibold mb-2">Understanding Zoning Classifications</h4>
            <div className="space-y-2">
              <div>
                <p className="font-semibold text-sm">Residential Zones</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• <strong>R1 (Single-Family):</strong> Detached homes only, larger lots (5,000+ sq ft)</li>
                  <li>• <strong>R2 (Two-Family):</strong> Duplexes, townhomes, smaller lots allowed</li>
                  <li>• <strong>R3 (Multi-Family):</strong> Apartments, condos, higher density</li>
                  <li>• <strong>PUD (Planned Unit Development):</strong> Mixed housing types in one development</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-sm">Commercial & Other Zones</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• <strong>C1 (Neighborhood Commercial):</strong> Small retail, offices</li>
                  <li>• <strong>C2 (General Commercial):</strong> Restaurants, shopping centers</li>
                  <li>• <strong>I (Industrial):</strong> Manufacturing, warehouses</li>
                  <li>• <strong>AG (Agricultural):</strong> Farming, ranching, low-density rural</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Houston's Unique System: Deed Restrictions</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Houston is the largest U.S. city without zoning ordinances. Instead, land use is controlled by:
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• <strong>Deed Restrictions:</strong> Private covenants recorded with property deed</li>
              <li>• <strong>Chapter 245:</strong> Texas law allowing deed restriction enforcement</li>
              <li>• <strong>Minimum Lot Sizes:</strong> Set by deed (not city ordinance)</li>
              <li>• <strong>Building Lines:</strong> Setback requirements in deed</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              <strong>Critical:</strong> In Houston, ALWAYS review deed restrictions before buying. 
              They expire after 25 years unless renewed by property owners.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">HOA Rules & Restrictions</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Homeowners Associations can impose restrictions beyond zoning:
            </p>
            <div className="space-y-2">
              <div>
                <p className="font-semibold text-sm">Common HOA Restrictions:</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Exterior paint colors must be pre-approved</li>
                  <li>• Fence height/material specifications</li>
                  <li>• No commercial vehicles parked in driveway</li>
                  <li>• Rental restrictions (minimum 6-12 month leases)</li>
                  <li>• No short-term rentals (Airbnb) prohibited</li>
                  <li>• Landscaping standards (lawn maintenance)</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-sm">Before You Buy:</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Request HOA bylaws, covenants, and restrictions</li>
                  <li>• Review last 2 years of meeting minutes</li>
                  <li>• Check for pending special assessments</li>
                  <li>• Verify rental restrictions if planning to rent</li>
                  <li>• Ask about violation enforcement history</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Agricultural Property Tax Valuation</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Texas offers agricultural exemptions (1-d-1 valuation) that can significantly reduce property taxes:
            </p>
            <div className="space-y-2">
              <div>
                <p className="font-semibold text-sm">Requirements:</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Minimum acreage: 10-20 acres (varies by county intensity)</li>
                  <li>• Primary use must be agricultural (not just hobby farm)</li>
                  <li>• Continuous ag use for 5+ years recommended</li>
                  <li>• Must generate income or be used for personal consumption</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-sm">Qualified Agricultural Uses:</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Cattle raising (1 cow per 5-15 acres typical)</li>
                  <li>• Hay production</li>
                  <li>• Beekeeping (6+ hives)</li>
                  <li>• Wildlife management (alternative to traditional ag)</li>
                  <li>• Horse boarding (commercial operation)</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-sm text-red-600">Warning: Rollback Taxes</p>
                <p className="text-sm text-muted-foreground">
                  If you change use from agricultural to residential, you'll owe rollback taxes 
                  for the previous 5 years (difference between ag value and market value plus interest).
                </p>
              </div>
            </div>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Building Setbacks & Restrictions</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Setbacks determine how close you can build to property lines:
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• <strong>Front Setback:</strong> 20-25 ft typical (varies by street type)</li>
              <li>• <strong>Side Setback:</strong> 5-10 ft (corner lots may have larger requirement)</li>
              <li>• <strong>Rear Setback:</strong> 15-20 ft typical</li>
              <li>• <strong>Height Restrictions:</strong> 35 ft residential, 45 ft with pitched roof</li>
              <li>• <strong>Impervious Cover:</strong> Max 50-65% of lot (includes house, driveway, patio)</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Extra-Territorial Jurisdiction (ETJ)</h4>
            <p className="text-sm text-muted-foreground">
              Texas cities can regulate development up to 5 miles outside city limits (ETJ). 
              Properties in ETJ are subject to city building codes but don't pay city taxes or receive city services. 
              Cities can annex ETJ areas, bringing them into city limits and tax base.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">How to Research Your Property</h4>
            <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
              <li>Check county tax assessor website for zoning classification</li>
              <li>Review plat map and deed restrictions at county clerk</li>
              <li>Request survey showing setbacks and easements</li>
              <li>Contact city planning department for zoning verification</li>
              <li>Review HOA documents (if applicable)</li>
              <li>Check for pending zoning changes or variances</li>
            </ol>
          </section>
        </div>
      </EducationalDialog>
    </>
  );
};
